import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { useGetLynFormPropertyMutation } from 'store/features/process/processApi';

dayjs.extend(utc);

export const utcConverter = (dayjsDate, day = false) => {
	if (day) {
		const year = dayjsDate?.year();
		const month = dayjsDate?.month();

		return dayjs?.utc().year(year).month(month).date(1).startOf('day');
	}

	const d = dayjs(dayjsDate);

	const dateStr = d?.format('YYYY-MM-DD');

	return dayjs?.utc(dateStr, 'YYYY-MM-DD');
};

export const hasGlKey = (data) => {
	if (!data || typeof data !== 'object') {
		return false;
	}

	return Object.keys(data).some((key) => key.startsWith('gl_'));
};

export const useControlData = (processCode) => {
	const userId = useSelector((state) => state.profile.profileData.userId);
	const companyCode = useSelector((state) => state.profile.profileData.creatorOrganizationId);

	const [GetLynFormProperty] = useGetLynFormPropertyMutation();

	const [data, setData] = useState({
		leaseExpense: [],
		glBankAccount: [],
		glAdvanceRent: [],
		depreciationRoU: [],
		glleaseLiability: [],
		glInterestExpense: [],
		accountsPayableHead: [],
		securityDepositHead: [],
		accumDepreciationRoU: [],
		rightOfUseAssetValue: [],
		leaseExpenseCostCenter: [],
		costCenterInterestExpense: [],
		leasePayableControlAccount: [],
		depreciationRoUCostCenter: [],
	});

	useEffect(() => {
		const fetchLynFormProperty = (code) =>
			GetLynFormProperty({
				type: 'FORM',
				companyCode,
				status: 'Active',
				propertyCode: code,
				fieldCode: processCode,
			});

		Promise.all([
			fetchLynFormProperty('PROPERTY_rightOfUseAssetValue'),
			fetchLynFormProperty('PROPERTY_glleaseLiability'),
			fetchLynFormProperty('PROPERTY_costCenterInterestExpense'),
			fetchLynFormProperty('PROPERTY_glInterestExpense'),
			fetchLynFormProperty('PROPERTY_leaseExpenseCostCenter'),
			fetchLynFormProperty('PROPERTY_leaseExpense'),
			fetchLynFormProperty('PROPERTY_leasePayableControlAccount'),
			fetchLynFormProperty('PROPERTY_glAdvanceRent'),
			fetchLynFormProperty('PROPERTY_depreciationRoUCostCenter'),
			fetchLynFormProperty('PROPERTY_depreciationRoU'),
			fetchLynFormProperty('PROPERTY_accumDepreciationRoU'),
			fetchLynFormProperty('PROPERTY_glBankAccount'),
			fetchLynFormProperty('PROPERTY_securityDepositHead'),
			fetchLynFormProperty('PROPERTY_accountsPayableHead'),
		])
			.then(([res1, res2, res3, res4, res5, res6, res7, res8, res9, res10, res11, res12, res13, res14]) => {
				setData({
					rightOfUseAssetValue: res1?.data?.lynProperties,
					glleaseLiability: res2?.data?.lynProperties,
					costCenterInterestExpense: res3?.data?.lynProperties,
					glInterestExpense: res4?.data?.lynProperties,
					leaseExpenseCostCenter: res5?.data?.lynProperties,
					leaseExpense: res6?.data?.lynProperties,
					leasePayableControlAccount: res7?.data?.lynProperties,
					glAdvanceRent: res8?.data?.lynProperties,
					depreciationRoUCostCenter: res9?.data?.lynProperties,
					depreciationRoU: res10?.data?.lynProperties,
					accumDepreciationRoU: res11?.data?.lynProperties,
					glBankAccount: res12?.data?.lynProperties,
					securityDepositHead: res13?.data?.lynProperties,
					accountsPayableHead: res14?.data?.lynProperties,
				});
			})
			.catch((err) => {
				console.error('Error fetching data:', err);
			});
	}, [userId]);

	return { lynFormProperties: data };
};
export const formatNumber = (value, decimalPlaces = 2) => {
	if (value === null || value === undefined) {
		return '0.00';
	}

	const num = typeof value === 'string' ? parseFloat(value) : Number(value);

	if (isNaN(num)) {
		return '0.00';
	}

	return num.toLocaleString('en-US', {
		minimumFractionDigits: decimalPlaces,
		maximumFractionDigits: decimalPlaces,
	});
};

export const processArrays = (grossRents, advanceAmounts) => {
	const originalGrossRentsLength = grossRents?.length;
	const originalAdvanceAmountsLength = advanceAmounts?.length;

	const effectiveLength = Math.max(originalGrossRentsLength, originalAdvanceAmountsLength);

	const paddedGrossRents = [...grossRents];
	const paddedAdvanceAmounts = [...advanceAmounts];

	if (originalGrossRentsLength < effectiveLength) {
		paddedGrossRents.push(...Array(effectiveLength - originalGrossRentsLength).fill(0));
	}

	if (originalAdvanceAmountsLength < effectiveLength) {
		paddedAdvanceAmounts.push(...Array(effectiveLength - originalAdvanceAmountsLength).fill(0));
	}

	return {
		effectiveLength: effectiveLength,
		processedGrossRents: paddedGrossRents,
		processedAdvanceAmounts: paddedAdvanceAmounts,
		effectiveAdvanceDuration: originalAdvanceAmountsLength,
	};
};

export const stateUpdater = (setStateFn) => {
	return (path, value) => {
		setStateFn((prev) => {
			const pathArray = typeof path === 'string' ? path.split('.') : path;

			if (pathArray.length === 0) return value;

			const result = { ...(typeof prev === 'object' ? prev : {}) };

			let current = result;

			for (let i = 0; i < pathArray.length - 1; i++) {
				const key = pathArray[i];

				current[key] = current[key] && typeof current[key] === 'object' ? { ...current[key] } : {};
				current = current[key];
			}

			const finalKey = pathArray[pathArray.length - 1];

			if (typeof value === 'object' && value !== null && typeof current[finalKey] === 'object' && current[finalKey] !== null) {
				current[finalKey] = { ...current[finalKey], ...value };
			} else {
				current[finalKey] = value;
			}

			return result;
		});
	};
};

export const isSequential = (objects) => {
	if (!objects || objects.length === 0) {
		return true;
	}

	for (let i = 0; i < objects.length; i++) {
		const current = objects[i];

		const to = parseInt(current.to);
		const from = parseInt(current.from);

		if (from > to) {
			return false;
		}

		if (i > 0) {
			const previous = objects[i - 1];
			const previousTo = parseInt(previous.to);

			if (from !== previousTo + 1) {
				return false;
			}
		}
	}
	return true;
};

export const isGrossRentLessThanAdvanceOnEachItem = (grossRent, advanceAmount) => {
	if (grossRent?.length !== advanceAmount?.length) {
		// throw new Error('Arrays must have the same length');

		return false;
	}

	return grossRent?.every((element, index) => element >= advanceAmount[index]);
};

export const transformToGrossRentListSet = (inputData) => {
	const grossRentListSet = [];

	function formatAmount(amount) {
		return amount.toLocaleString('en-US', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		});
	}

	Object.keys(inputData).forEach((dateKey) => {
		const leaseData = inputData[dateKey];
		const formattedGroup = [];

		leaseData.forEach((item) => {
			const formattedItem = {
				from: item['Rent (From Month)'],
				to: item['Rent (To Month)'].toString(),
				amount: formatAmount(item['Rent Amount']),
				remarks: '',
			};

			formattedGroup.push(formattedItem);
		});

		if (formattedGroup.length > 0) {
			grossRentListSet.push(formattedGroup);
		}
	});

	return grossRentListSet;
};
